import { ArrowUpRight, Compass, Flag, Plus } from "lucide-react";

import type { GtmBrainDoc } from "../api/GtmApi";
import { listCompetitors } from "./competitors";

type Props = {
  docs: GtmBrainDoc[];
  onOpenCompetitor: (key: string) => void;
  onAddCompetitor: () => void;
  onBackToOverview: () => void;
};

export default function BrainCompetitorsPanel({
  docs,
  onOpenCompetitor,
  onAddCompetitor,
  onBackToOverview,
}: Props) {
  const competitors = listCompetitors(docs);

  return (
    <div className="flex h-full w-[22rem] shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-4 py-3 text-xs text-slate-500">
        <button
          type="button"
          className="hover:text-slate-800 hover:underline"
          onClick={onBackToOverview}
        >
          Brain
        </button>
        <span className="mx-1.5 text-slate-300">›</span>
        <span className="font-medium text-slate-800">Competitors</span>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <div className="mb-1 flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-50">
                <Compass className="h-4 w-4 text-orange-600" />
              </span>
              <h2 className="text-base font-semibold text-slate-900">Competitors</h2>
            </div>
            <p className="text-sm text-slate-500">
              The alternatives your market compares you with.
            </p>
          </div>
          <button
            type="button"
            onClick={onAddCompetitor}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add reference
          </button>
        </div>

        {competitors.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-slate-50/60 px-6 py-14 text-center">
            <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-100">
              <Flag className="h-5 w-5 text-orange-500" />
            </span>
            <p className="text-sm font-medium text-slate-800">Nothing in Competitors yet.</p>
            <p className="mt-1 text-sm text-slate-500">
              Use &apos;Add reference&apos; above to start the first one.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {competitors.map((c) => (
              <li key={c.key}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 px-3 py-3.5 text-left transition-colors hover:bg-slate-50"
                  onClick={() => onOpenCompetitor(c.key)}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-50">
                    <Flag className="h-4 w-4 text-orange-600" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-slate-900">
                      {c.label}
                    </span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {c.filled} of {c.total} sections filled
                    </span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
